import { Team } from '../types/GameplayTypes';
import { devSwitch } from '../util/Environment';
import { GameplayState, gamePlayStore, useGameplaySelector } from './GameplayStore';
import { store } from './Store';

export type Command = {
	id: string;
	label: string;
	key: string;
	/** True if the command overrides the standard game rules */
	ruleOverride?: boolean;
	/** Message shown in a confirmation dialog before the action runs. Undefined if no confirmation is required. */
	confirmMessage?: string;
	action: () => unknown;
};

export type CommandResult = {
	commands: Command[];
	/** The number of answer commands available. Undefined if not currently accepting answers. */
	answerCount: number | undefined;
	title: string;
};

export type CommandId = typeof ALL_COMMANDS[number]['id'];

const ALL_COMMANDS = [{
	id: 'buzzInTeamA',
	label: 'Blue Buzzed',
	key: 'B',
	action: () => store.dispatch(gamePlayStore.actions.faceOffBuzz({ team: Team.A }))
}, {
	id: 'buzzInTeamB',
	label: 'Red Buzzed',
	key: 'R',
	action: () => store.dispatch(gamePlayStore.actions.faceOffBuzz({ team: Team.B }))
}, {
	id: 'play',
	label: 'Play',
	key: 'P',
	action: () => store.dispatch(gamePlayStore.actions.playOrPass('play')),
}, {
	id: 'pass',
	label: 'Pass',
	key: 'S',
	action: () => store.dispatch(gamePlayStore.actions.playOrPass('pass')),

}, {
	id: 'wrongAnswer',
	label: 'Wrong Answer',
	key: 'X',
	action: () => store.dispatch(gamePlayStore.actions.wrongAnswer()),
}, {
	id: 'revealQuestion',
	label: 'Show Question',
	key: 'Q',
	action: () => store.dispatch(gamePlayStore.actions.revealQuestion()),
}, {
	id: 'revealAll',
	label: 'Reveal All',
	key: 'V',
	action: () => store.dispatch(gamePlayStore.actions.revealAll()),
}, {
	id: 'nextQuestion',
	label: 'Next Round',
	key: 'N',
	action: () => store.dispatch(gamePlayStore.actions.nextRound()),
}, {
	id: 'finishGame',
	label: 'Finish Game',
	key: 'F',
	action: () => store.dispatch(gamePlayStore.actions.nextRound()),
}, {
	id: 'switchTeam',
	label: 'Switch Teams',
	key: 'T',
	ruleOverride: true,
	action: () => store.dispatch(gamePlayStore.actions.switchTeam()),
}, {
	id: 'restart',
	label: 'Restart Game',
	key: 'R',
	ruleOverride: true,
	confirmMessage: 'This will reset the scores and start the game over from the first round. Are you sure you want to restart?',
	action: () => store.dispatch(gamePlayStore.actions.restartGame()),
}, {
	id: 'clearStrikes',
	label: 'Clear Strikes',
	key: 'C',
	ruleOverride: true,
	action: () => store.dispatch(gamePlayStore.actions.clearStrikes())
}] as const satisfies readonly Command[];

export function selectCommands(state: GameplayState): CommandResult {
	const { controllingTeam, phase, round } = state;
	const questionVisible = gamePlayStore.selectors.questionVisible({ gamePlay: state });
	const question = gamePlayStore.selectors.currentQuestion({ gamePlay: state });
	const lastRound = state.questions.length - 1;
	let answerCount = undefined;
	let title = 'Commands';

	const ids: CommandId[] = [];

	switch (phase) {
	case 'faceoff':
		if (questionVisible) {
			if (controllingTeam === undefined) {
				ids.push('buzzInTeamA', 'buzzInTeamB');
				title = 'Face Off - Buzz';
			} else {
				ids.push('wrongAnswer', 'switchTeam');
				answerCount = question?.answers.length;
				title = 'Face Off - Answer';
			}
		} else {
			ids.push('revealQuestion');
			title = 'Face Off - Buzz';
		}
		break;

	case 'playOrPass':
		ids.push('play', 'pass', 'switchTeam');
		title = 'Play or Pass';
		break;

	case 'answering':
		ids.push('wrongAnswer', 'switchTeam');
		answerCount = question?.answers.length;
		title = 'Play the Board';
		break;

	case 'steal':
		ids.push('wrongAnswer');
		answerCount = question?.answers.length;
		title = 'Steal';
		break;

	case 'reveal':
		ids.push('revealAll');
		answerCount = question?.answers.length;
		title = 'Reveal Answers';
		break;

	case 'roundEnd':
		ids.push('restart');
		title = 'Round End';
		break;

	case 'gameEnd':
		ids.push('restart');
		title = 'Game End';
		break;
	}

	ids.push((round < lastRound) ? 'nextQuestion' : 'finishGame');

	if (devSwitch(false)) {
		ids.push('clearStrikes');
	}

	return {
		commands: ids.map(commandWithId),
		answerCount,
		title,
	};
}

function commandWithId(id: CommandId): Command {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	return ALL_COMMANDS.find(c => c.id === id)!;
}

export function useGameCommands() {
	return useGameplaySelector(rootState => selectCommands(rootState.gamePlay));
}
